import axios from "axios";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "jea_auth";

if (!BASE_URL) {
  // Fail loudly in the console — the platform requires this env.
  // eslint-disable-next-line no-console
  console.error("[JEA] REACT_APP_BACKEND_URL is not set");
}

export const readAuth = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const writeAuth = (auth) => {
  if (!auth) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
  }
};

export const apiClient = axios.create({
  baseURL: `${BASE_URL || ""}/api`,
  headers: { "Content-Type": "application/json" },
});

let refreshingPromise = null;

const runRefresh = async () => {
  const auth = readAuth();
  if (!auth?.refresh_token) throw new Error("No refresh token");
  const { data } = await axios.post(`${BASE_URL}/api/auth/refresh`, {
    refresh_token: auth.refresh_token,
  });
  writeAuth({
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    user: data.user,
  });
  return data.access_token;
};

apiClient.interceptors.request.use((config) => {
  const auth = readAuth();
  if (auth?.access_token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${auth.access_token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (r) => r,
  async (error) => {
    const { response, config } = error;
    if (!response || !config) return Promise.reject(error);

    // 402 → user is in preview mode. Emit a global event so the shell can show the paywall.
    if (response.status === 402 && typeof window !== "undefined") {
      try {
        window.dispatchEvent(new CustomEvent("jea:paywall", {
          // The backend's `detail` is a full user-facing sentence; pass it as
          // `message` so the modal shows it verbatim and skips its default prefix.
          detail: { feature: "this feature", message: response.data?.detail || "" },
        }));
      } catch { /* ignore */ }
    }

    // Don't loop refresh calls
    const isAuthCall = /\/auth\/(login|register|2fa\/verify|refresh|forgot-password)$/.test(
      config.url || ""
    );
    if (response.status === 401 && !config._retry && !isAuthCall) {
      config._retry = true;
      try {
        refreshingPromise = refreshingPromise || runRefresh();
        const newAccess = await refreshingPromise;
        refreshingPromise = null;
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${newAccess}`;
        return apiClient(config);
      } catch (e) {
        refreshingPromise = null;
        writeAuth(null);
        if (typeof window !== "undefined") {
          const isAdminPath = window.location.pathname.startsWith("/admin");
          const target = isAdminPath ? "/admin/login" : "/login";
          if (window.location.pathname !== target) window.location.assign(target);
        }
      }
    }
    return Promise.reject(error);
  }
);

export const formatApiError = (err, fallback = "Something went wrong. Please try again.") => {
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (typeof d === "object") {
    // Phase 10D structured errors: { message, code, alternatives }
    if (typeof d.message === "string") return d.message;
    if (typeof d.msg === "string") return d.msg;
  }
  return String(d);
};

/** Phase 10D — extract the full structured detail (code + alternatives) from
 * a FastAPI error response. Returns null when the detail isn't an object. */
export const extractApiErrorMeta = (err) => {
  const d = err?.response?.data?.detail;
  if (d && typeof d === "object" && !Array.isArray(d)) {
    return {
      code: typeof d.code === "string" ? d.code : null,
      message: typeof d.message === "string" ? d.message : null,
      alternatives: Array.isArray(d.alternatives) ? d.alternatives : null,
      status: err?.response?.status ?? null,
    };
  }
  return { code: null, message: null, alternatives: null, status: err?.response?.status ?? null };
};

export const authApi = {
  login: (identifier, password) =>
    apiClient.post("/auth/login", { identifier, password }).then((r) => r.data),
  register: (payload) => apiClient.post("/auth/register", payload).then((r) => r.data),
  verify2fa: (pending_token, code, channel) =>
    apiClient.post("/auth/2fa/verify", { pending_token, code, channel }).then((r) => r.data),
  verify2faCode: (pending_token, channel, code) =>
    apiClient.post("/auth/2fa/verify-code", { pending_token, channel, code }).then((r) => r.data),
  send2faCode: (pending_token, channel) =>
    apiClient.post("/auth/2fa/send-code", { pending_token, channel }).then((r) => r.data),
  me: () => apiClient.get("/auth/me").then((r) => r.data),
  logout: (refresh_token) =>
    apiClient.post("/auth/logout", { refresh_token }).then((r) => r.data),
  forgotPassword: (identifier) =>
    apiClient.post("/auth/forgot-password", { identifier }).then((r) => r.data),
  // Phase 10F — reset password via EITHER magic-link `token` OR
  // `identifier` + `code` (SMS or email code path).
  resetPassword: (payload) =>
    apiClient.post("/auth/reset-password", payload).then((r) => r.data),
  // Phase 10F — passwordless OTP sign-in.
  passwordlessRequestCode: (identifier) =>
    apiClient
      .post("/auth/passwordless/request-code", { identifier })
      .then((r) => r.data),
  passwordlessVerifyCode: (identifier, code) =>
    apiClient
      .post("/auth/passwordless/verify-code", { identifier, code })
      .then((r) => r.data),
  // Phase 10E — post-registration contact verification
  verifyContact: (payload) =>
    apiClient.post("/auth/register/verify-contact", payload).then((r) => r.data),
  resendContactCode: (pending_token, channel) =>
    apiClient
      .post("/auth/register/resend-contact-code", { pending_token, channel })
      .then((r) => r.data),
  adminLogin: (email, password) =>
    apiClient.post("/admin/auth/login", { email, password }).then((r) => r.data),
  adminVerify2fa: (pending_token, code) =>
    apiClient.post("/admin/auth/2fa/verify", { pending_token, code }).then((r) => r.data),
};

// Phase 10E — Contact Methods panel (Settings)
export const contactApi = {
  get: () => apiClient.get("/account/contact").then((r) => r.data),
  updateEmail: (email) =>
    apiClient.post("/account/contact/update-email", { email }).then((r) => r.data),
  confirmEmail: (code) =>
    apiClient.post("/account/contact/confirm-email", { code }).then((r) => r.data),
  updatePhone: (phone) =>
    apiClient.post("/account/contact/update-phone", { phone }).then((r) => r.data),
  confirmPhone: (code) =>
    apiClient.post("/account/contact/confirm-phone", { code }).then((r) => r.data),
  removeEmail: () =>
    apiClient.post("/account/contact/remove-email").then((r) => r.data),
  removePhone: () =>
    apiClient.post("/account/contact/remove-phone").then((r) => r.data),
  setVoiceFallback: (enabled) =>
    apiClient.post("/account/voice-fallback", { enabled }).then((r) => r.data),
  dismissAddPhoneBanner: () =>
    apiClient.post("/account/add-phone-banner-dismiss").then((r) => r.data),
};

export const twoFactorApi = {
  status: () => apiClient.get("/account/2fa/status").then((r) => r.data),
  enrollPhone: (phone) =>
    apiClient.post("/account/2fa/enroll/phone", { phone }).then((r) => r.data),
  enrollStart: (payload) =>
    apiClient.post("/account/2fa/enroll/start", payload).then((r) => r.data),
  enrollConfirm: (payload) =>
    apiClient.post("/account/2fa/enroll/confirm", payload).then((r) => r.data),
  totpStart: () => apiClient.post("/account/2fa/enroll/totp-start").then((r) => r.data),
  totpConfirm: (payload) =>
    apiClient.post("/account/2fa/enroll/totp-confirm", payload).then((r) => r.data),
  regenerateBackupCodes: () =>
    apiClient.post("/account/2fa/backup-codes/regenerate").then((r) => r.data),
  setPrimary: (channel) =>
    apiClient.post("/account/2fa/primary", { channel }).then((r) => r.data),
  unenroll: (channel) =>
    apiClient.delete(`/account/2fa/${channel}`).then((r) => r.data),
};

export const adminRecoveryApi = {
  reset2fa: (userId, reason) =>
    apiClient.post(`/admin/users/${userId}/reset-2fa`, { reason }).then((r) => r.data),
  sendPasswordReset: (userId, reason) =>
    apiClient.post(`/admin/users/${userId}/send-password-reset`, { reason }).then((r) => r.data),
  // Phase 10F — SMS variant of send-password-reset (iteration 35).
  sendPasswordResetSms: (userId, reason) =>
    apiClient.post(`/admin/users/${userId}/send-password-reset-sms`, { reason }).then((r) => r.data),
  fullRecovery: (userId, reason, channel) =>
    apiClient
      .post(`/admin/users/${userId}/full-recovery`,
            channel ? { reason, channel } : { reason })
      .then((r) => r.data),
  auditLog: (limit = 50, offset = 0) =>
    apiClient.get("/admin/users/recovery-audit-log", { params: { limit, offset } }).then((r) => r.data),
};

export const magicLinkApi = {
  validate: (token) =>
    apiClient.get("/auth/magic-link/validate", { params: { token } }).then((r) => r.data),
  consume: (token, new_password) =>
    apiClient.post("/auth/magic-link/consume", { token, new_password }).then((r) => r.data),
};
